// @flow

import * as React from 'react';
import {Helmet} from 'react-helmet-async';
import Box from 'sail/Box';
import {ContentSkeleton, OnlyRenderOnClient} from 'docs/components';
import {useAccount, useLocation} from 'docs/data';
import type {IntegrationBuilderType} from 'docs/data/location/Location';
import {useIntl} from 'react-intl';
import {GuideProvider} from './data/GuideProvider';
import {ContentProvider} from './data/ContentProvider';
import {DataProvider} from './data/DataProvider';
import Guide from './components/Guide';
import content from './content';

import './css/IntegrationBuilder.scss';

const LoadingSkeleton = () => (
  <Box
    flex={{direction: 'column'}}
    flexGrow={1}
    style={{paddingLeft: '5vw', paddingRight: '5vw'}}
  >
    <Box
      background="surface"
      style={{
        height: '40px',
        width: '100%',
        marginTop: '32px',
        marginBottom: '32px',
        borderRadius: '4px',
      }}
    />
    <Box flex={{direction: 'row'}} flexGrow={1} style={{width: '100%'}}>
      <ContentSkeleton />
      <Box
        background="surface"
        flexGrow={1}
        style={{
          width: '70%',
          marginLeft: '4vw',
          marginTop: '8px',
          borderRadius: '8px',
        }}
      />
    </Box>
  </Box>
);

type IntegrationBuilderProps = {|
  containerRef: React.Ref<any>,
|};

export const IntegrationBuilder = ({containerRef}: IntegrationBuilderProps) => {
  const {account} = useAccount();
  const {location} = useLocation();
  const intl = useIntl();

  const integrationType: ?IntegrationBuilderType = location.integrationBuilderType();

  if (!content || !integrationType || !content[integrationType]) {
    return null;
  }
  const {sections, integrations, copy, languages, demos} = content[
    integrationType
  ];

  // TODO(matv): Integration Builder does not include any search metadata

  return (
    <Box
      id="integration-builder"
      className="IntegrationBuilder"
      background="gray50"
      flex={{direction: 'column'}}
      flexGrow={1}
    >
      <Helmet htmlAttributes={{style: 'width: 100vw'}}>
        <title>{intl.formatMessage(copy.title.message)}</title>
        <meta name="viewport" content="" />
      </Helmet>
      <OnlyRenderOnClient placeholder={<LoadingSkeleton />}>
        <DataProvider>
          <ContentProvider
            integrations={integrations}
            sections={sections}
            copy={copy}
            integrationType={integrationType}
          >
            <GuideProvider
              merchant={account.merchant?.isLoggedIn}
              keys={account.keys}
              languages={languages}
              demos={demos}
              containerRef={containerRef}
              siteCountry={account.country.code}
            >
              <Guide />
            </GuideProvider>
          </ContentProvider>
        </DataProvider>
      </OnlyRenderOnClient>
    </Box>
  );
};
